import { stringify } from 'safe-stable-stringify';
import { match } from 'ts-pattern';

import type { BoResourceEndpointMap } from '@Common/types/bo/resource_endpoint_map';
import type { BoExternalResourceEndpointMap } from '@Common/types/bo_external/resource_endpoint_map';

import { Resources } from '@sh4res/bo-common/utils';

type IdFormatter = (id: string | number) => string;

// Some resources use an id that isn't the raw record id (e.g. a formatted/prefixed
// transfer id)
const ID_FORMATTERS: Partial<Record<Resources, IdFormatter>> = {};

// Per-resource valid tab names, so `tab` can be type-checked once a resource's show
// page opts in here. Resources without an entry fall back to a plain string.
type TabsByResource = {
  // bo/src/resources/users/UsersShow.tsx (VerticalTab `path` values)
  users:
    | 'pending_actions'
    | 'portfolio_gia'
    | 'orders_gia'
    | 'investment_plans_gia'
    | 'details_gia'
    | 'portfolio_pea'
    | 'orders_pea'
    | 'investment_plans_pea'
    | 'details_pea'
    | 'portfolio_per'
    | 'orders_per'
    | 'investment_plans_per'
    | 'details_per'
    | 'transfers'
    | 'support_marketing'
    | 'documents'
    | 'social'
    | 'logs'
    | 'payment_information'
    | 'approbation_states'
    | 'external_accounts';
};

type TabFor<R extends Resources> = R extends keyof TabsByResource ? TabsByResource[R] : string;

type QueryParams = Record<string, string | number>;

type ShowParams<R extends Resources> = {
  tab?: TabFor<R>;
  query?: QueryParams;
};

type CreateParams = {
  query?: QueryParams;
};

type ListSort = { field: string; order: 'ASC' | 'DESC' };

// A resource can exist in both BO and BO external, in which case both list filters are accepted
type ListEndpointFor<R extends Resources> =
  | (R extends keyof BoResourceEndpointMap
      ? BoResourceEndpointMap[R] extends { list: infer Endpoint }
        ? Endpoint
        : never
      : never)
  | (R extends keyof BoExternalResourceEndpointMap
      ? BoExternalResourceEndpointMap[R] extends { list: infer Endpoint }
        ? Endpoint
        : never
      : never);

// The Client side is unusable here: the filter goes through z.preprocess (JSON string), whose input type is unknown
type FilterOf<Endpoint> = Endpoint extends {
  Server: { RequestQueryParams: { filter?: infer Filter } };
}
  ? Exclude<Filter, undefined>
  : never;

// Resources whose list isn't in the resource endpoint maps keep an untyped filter
type ListFilterFor<R extends Resources> = [ListEndpointFor<R>] extends [never]
  ? Record<string, unknown>
  : FilterOf<ListEndpointFor<R>>;

type ListParamsBase = {
  filter?: unknown;
  displayedFilters?: Record<string, boolean>;
  sort?: ListSort;
  page?: number;
  perPage?: number;
};

type ListParams<R extends Resources> = Omit<ListParamsBase, 'filter'> & {
  filter?: ListFilterFor<R>;
};

export function buildSearchParams(
  values: Record<string, string | number | boolean | undefined>,
): string {
  const entries = Object.entries(values).filter(
    (entry): entry is [string, string | number | boolean] => entry[1] !== undefined,
  );

  if (entries.length === 0) {
    return '';
  }

  return new URLSearchParams(entries.map(([key, value]) => [key, String(value)])).toString();
}

function buildListSearchParams(params: ListParamsBase | undefined): string {
  if (!params) {
    return '';
  }

  const entries: [string, string][] = [];

  if (params.filter) {
    entries.push(['filter', stringify(params.filter)]);
  }
  if (params.displayedFilters) {
    entries.push(['displayedFilters', stringify(params.displayedFilters)]);
  }
  if (params.sort) {
    entries.push(['sort', params.sort.field], ['order', params.sort.order]);
  }
  if (params.page !== undefined) {
    entries.push(['page', String(params.page)]);
  }
  if (params.perPage !== undefined) {
    entries.push(['perPage', String(params.perPage)]);
  }

  return new URLSearchParams(entries).toString();
}

/**
 * Builds a BO/BO-external route path for a resource
 */
export function buildLink<R extends Resources>(
  resource: R,
  mode: 'show',
  id: string | number,
  params?: ShowParams<R>,
): string;
export function buildLink(resource: Resources, mode: 'edit', id: string | number): string;
export function buildLink(resource: Resources, mode: 'create', params?: CreateParams): string;
export function buildLink<R extends Resources>(
  resource: R,
  mode: 'list',
  params?: ListParams<R>,
): string;
export function buildLink<R extends Resources>(
  resource: R,
  mode: 'show' | 'edit' | 'create' | 'list',
  idOrParams?: string | number | CreateParams | ListParams<R>,
  showParams?: ShowParams<R>,
): string {
  return match(mode)
    .with('show', () => {
      const id = idOrParams as string | number;
      const formattedId = ID_FORMATTERS[resource]?.(id) ?? id;
      const search = buildSearchParams({ tab: showParams?.tab, ...showParams?.query });
      return `/${resource}/${formattedId}/show${search ? `?${search}` : ''}`;
    })
    .with('edit', () => {
      const id = idOrParams as string | number;
      const formattedId = ID_FORMATTERS[resource]?.(id) ?? id;
      return `/${resource}/${formattedId}`;
    })
    .with('create', () => {
      const search = buildSearchParams({ ...(idOrParams as CreateParams | undefined)?.query });
      return `/${resource}/create${search ? `?${search}` : ''}`;
    })
    .with('list', () => {
      const search = buildListSearchParams(idOrParams as ListParamsBase | undefined);
      return `/${resource}${search ? `?${search}` : ''}`;
    })
    .exhaustive();
}
